/**
 * HeroSection - Marketplace Homepage Hero Component
 *
 * A prominent hero section for the SAUBHAGYA marketplace public landing page.
 * Features:
 * - Attractive gradient background with smooth animations
 * - Mission statement and platform description
 * - Search bar with icon for product discovery
 * - "Browse Products" call-to-action button
 * - Fully responsive design (mobile-first approach)
 * - Smooth transitions and hover effects
 * - Type-safe props interface
 *
 * @example
 * ```tsx
 * <HeroSection
 *   onSearch={(query) => console.log('Searching for:', query)}
 *   onBrowseClick={() => navigate('/marketplace/products')}
 * />
 * ```
 */

import React, { useState, FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Search, ArrowRight, Zap, Leaf, ShoppingBag } from 'lucide-react';

// ============================================================================
// Constants
// ============================================================================

/**
 * Mission statement for SAUBHAGYA platform
 */
const MISSION_TITLE = 'Empowering Sustainable Energy';

/**
 * Platform description
 */
const MISSION_DESCRIPTION =
  'Discover quality biogas products, equipment, and services from verified sellers across India. Join the green energy revolution with SAUBHAGYA.';

/**
 * Search input placeholder text
 */
const SEARCH_PLACEHOLDER = 'Search for biogas products, equipment, services...';

/**
 * Browse button text
 */
const BROWSE_BUTTON_TEXT = 'Browse All Products';

/**
 * Feature highlights
 */
const FEATURES = [
  { icon: Zap, label: 'Clean Energy' },
  { icon: Leaf, label: 'Eco-Friendly' },
  { icon: ShoppingBag, label: 'Quality Products' },
] as const;

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Props interface for HeroSection component
 */
export interface HeroSectionProps {
  /**
   * Callback when user performs a search
   * @param query - The search query string
   */
  onSearch?: (query: string) => void;

  /**
   * Callback when user clicks "Browse Products" button
   */
  onBrowseClick?: () => void;

  /**
   * Custom className for root element
   */
  className?: string;

  /**
   * Custom mission title (overrides default)
   */
  missionTitle?: string;

  /**
   * Custom mission description (overrides default)
   */
  missionDescription?: string;

  /**
   * Custom search placeholder text
   */
  searchPlaceholder?: string;

  /**
   * Hide the feature highlights section
   */
  hideFeatures?: boolean;

  /**
   * Loading state for search
   */
  isSearching?: boolean;
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * Feature highlight item component
 */
const FeatureItem: React.FC<{
  icon: React.ElementType;
  label: string;
}> = ({ icon: Icon, label }) => (
  <div className="flex items-center space-x-2 text-white/90 hover:text-white transition-colors duration-200">
    <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
      <Icon className="h-4 w-4" />
    </div>
    <span className="text-sm font-medium">{label}</span>
  </div>
);

/**
 * Animated background gradient component
 */
const AnimatedBackground: React.FC = () => (
  <div className="absolute inset-0 overflow-hidden">
    {/* Base gradient */}
    <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900" />

    {/* Animated overlay gradients */}
    <div className="absolute inset-0 bg-gradient-to-tr from-purple-600/20 via-transparent to-green-500/20 animate-pulse" />

    {/* Decorative circles */}
    <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" />
    <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000" />
  </div>
);

/**
 * Search form component
 */
const SearchForm: React.FC<{
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: (e: FormEvent) => void;
  placeholder: string;
  isSearching?: boolean;
}> = ({ searchQuery, onSearchChange, onSearchSubmit, placeholder, isSearching }) => (
  <form
    onSubmit={onSearchSubmit}
    className="relative w-full max-w-2xl mx-auto"
    role="search"
    aria-label="Product search"
  >
    <div className="relative flex items-center">
      {/* Search icon */}
      <div className="absolute left-4 pointer-events-none">
        <Search
          className={cn(
            'h-5 w-5 text-gray-400 transition-colors duration-200',
            searchQuery && 'text-blue-500'
          )}
        />
      </div>

      {/* Search input */}
      <Input
        type="text"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={placeholder}
        disabled={isSearching}
        className={cn(
          'w-full h-14 pl-12 pr-32 text-base',
          'bg-white/95 backdrop-blur-sm',
          'border-2 border-white/20',
          'focus:border-white focus:bg-white',
          'rounded-xl shadow-2xl',
          'transition-all duration-300',
          'placeholder:text-gray-400',
          isSearching && 'opacity-60 cursor-not-allowed'
        )}
        aria-label="Search products"
      />

      {/* Search button */}
      <Button
        type="submit"
        size="lg"
        disabled={!searchQuery.trim() || isSearching}
        className={cn(
          'absolute right-2 h-10',
          'bg-blue-600 hover:bg-blue-700',
          'text-white font-medium',
          'rounded-lg shadow-lg',
          'transition-all duration-200',
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
      >
        {isSearching ? 'Searching...' : 'Search'}
      </Button>
    </div>
  </form>
);

// ============================================================================
// Main Component
// ============================================================================

/**
 * HeroSection - Main hero section component for marketplace homepage
 *
 * Provides a visually appealing entry point for the marketplace with search
 * functionality and clear call-to-action.
 */
export const HeroSection: React.FC<HeroSectionProps> = ({
  onSearch,
  onBrowseClick,
  className,
  missionTitle = MISSION_TITLE,
  missionDescription = MISSION_DESCRIPTION,
  searchPlaceholder = SEARCH_PLACEHOLDER,
  hideFeatures = false,
  isSearching = false,
}) => {
  // ============================================================================
  // State Management
  // ============================================================================

  const [searchQuery, setSearchQuery] = useState<string>('');

  // ============================================================================
  // Event Handlers
  // ============================================================================

  /**
   * Handle search form submission
   */
  const handleSearchSubmit = (e: FormEvent): void => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();

    if (trimmedQuery && onSearch) {
      onSearch(trimmedQuery);
    }
  };

  /**
   * Handle browse products button click
   */
  const handleBrowseClick = (): void => {
    if (onBrowseClick) {
      onBrowseClick();
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <section
      className={cn(
        'relative min-h-screen w-full',
        'flex items-center justify-center',
        'overflow-hidden',
        className
      )}
      aria-label="Hero section"
    >
      {/* Animated Background */}
      <AnimatedBackground />

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="text-center space-y-8 md:space-y-12">
          {/* Mission Statement */}
          <div className="space-y-4 md:space-y-6">
            {/* Title */}
            <h1
              className={cn(
                'text-4xl sm:text-5xl md:text-6xl lg:text-7xl',
                'font-bold tracking-tight',
                'text-white',
                'drop-shadow-2xl',
                'animate-in fade-in slide-in-from-bottom-4 duration-700'
              )}
            >
              {missionTitle}
            </h1>

            {/* Description */}
            <p
              className={cn(
                'text-lg sm:text-xl md:text-2xl',
                'text-white/90',
                'max-w-3xl mx-auto',
                'leading-relaxed',
                'drop-shadow-lg',
                'animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150'
              )}
            >
              {missionDescription}
            </p>
          </div>

          {/* Feature Highlights */}
          {!hideFeatures && (
            <div
              className={cn(
                'flex flex-wrap items-center justify-center gap-6 md:gap-8',
                'animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300'
              )}
            >
              {FEATURES.map((feature, index) => (
                <FeatureItem
                  key={index}
                  icon={feature.icon}
                  label={feature.label}
                />
              ))}
            </div>
          )}

          {/* Search Bar */}
          <div
            className={cn(
              'w-full',
              'animate-in fade-in slide-in-from-bottom-4 duration-700 delay-500'
            )}
          >
            <SearchForm
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onSearchSubmit={handleSearchSubmit}
              placeholder={searchPlaceholder}
              isSearching={isSearching}
            />
          </div>

          {/* Call-to-Action Button */}
          <div
            className={cn(
              'flex justify-center',
              'animate-in fade-in slide-in-from-bottom-4 duration-700 delay-700'
            )}
          >
            <Button
              onClick={handleBrowseClick}
              size="lg"
              className={cn(
                'h-14 px-8',
                'bg-white text-blue-600',
                'hover:bg-white/90 hover:scale-105',
                'font-semibold text-lg',
                'rounded-xl shadow-2xl',
                'transition-all duration-300',
                'group'
              )}
            >
              <span>{BROWSE_BUTTON_TEXT}</span>
              <ArrowRight
                className={cn(
                  'ml-2 h-5 w-5',
                  'transition-transform duration-300',
                  'group-hover:translate-x-1'
                )}
              />
            </Button>
          </div>

          {/* Trust Badge */}
          <div
            className={cn(
              'text-white/70 text-sm',
              'animate-in fade-in duration-1000 delay-1000'
            )}
          >
            <p>Trusted by 1000+ sellers and buyers across India</p>
          </div>
        </div>
      </div>

      {/* Bottom Gradient Overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 to-transparent" />
    </section>
  );
};

// ============================================================================
// Exports
// ============================================================================

export default HeroSection;
